import { PersonAbstractResource } from './../../models/abstract.model';
import { Injectable } from '@angular/core';
import { environment } from 'src/environments/environment';
import { MessageToastService } from '../common/message-toast.service';
import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Observable, EMPTY } from 'rxjs';
import { IPersonQueryResult, IPerson, IPersonForm, IacmacForm } from 'src/app/models/iperson.interface';
import { catchError, map, shareReplay } from 'rxjs/operators';
import { IKeyValue } from 'src/app/models/ikeyvalue.interface';
import { HTTP_CONTENT_HEADERS } from 'src/app/shared/data/const.data';

@Injectable({
  providedIn: 'root'
})
export class PersonService {

  private url = 'persons';
  private api = environment.apiUrl;

  constructor(private http: HttpClient, private msg: MessageToastService) { }

  getTable(filter: string): Observable<IPersonQueryResult> {
    return this.http.get<IPersonQueryResult>(`${this.api}${this.url}/table?${filter}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  search(filter: string): Observable<IPerson[]> {
    return this.http.get<IPerson[]>(`${this.api}${this.url}/search/${filter}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  getSingle(id: number): Observable<IPerson> {
    return this.http.get<IPerson>(`${this.api}${this.url}/${id}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  exportEmails(){
    const url = `${this.api}${this.url}/email`;

    let hdrs = new HttpHeaders();
    hdrs = hdrs.set('Accept', HTTP_CONTENT_HEADERS.Excel)

    return this.http.get(url,{ observe: 'response', headers: hdrs, responseType: 'blob' }).pipe(
      catchError(err => { this.msg.errorEx(err); return EMPTY; }),
      map((res) => (new Blob([res.body], { type: HTTP_CONTENT_HEADERS.Excel }))),
      shareReplay()
    );
  }

  getForm(id: number): Observable<IPersonForm> {
    return this.http.get<IPersonForm>(`${this.api}${this.url}/${id}/form`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  update(id: number, obj: IPersonForm): Observable<IPerson> {
    return this.http.put<IPerson>(`${this.api}${this.url}/${id}`, obj).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  add(obj: IPersonForm): Observable<IPerson> {
    return this.http.post<IPerson>(`${this.api}${this.url}/`, obj).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  delete(id: number) {
    return this.http.delete(`${this.api}${this.url}/${id}`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  setIacmacNumber(personId: number): Observable<IPerson> {
    const form = {iacmacNumber: '', iacmacDate: ''} as IacmacForm;
    return this.http.post<IPerson>(`${this.api}${this.url}/${personId}/iacmac`, form).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      })
    );
  }
  updateIacmacNumber(personId: number, form: IacmacForm): Observable<IPerson> {
    return this.http.put<IPerson>(`${this.api}${this.url}/${personId}/iacmac`, form).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      })
    );
  }

  importPersons(formData: FormData) {
    const urlUpload = `${this.api}${this.url}/import`;
    return this.http.post<any>(urlUpload, formData, { reportProgress: true, observe: 'events' });
  }

  getConferences(personId: number): Observable<IKeyValue[]> {
    return this.http.get<IKeyValue[]>(`${this.api}${this.url}/${personId}/conferences`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }

  getAbstracts(personId: number): Observable<PersonAbstractResource[]> {
    return this.http.get<PersonAbstractResource[]>(`${this.api}${this.url}/${personId}/abstracts`).pipe(
      catchError(err => {
        this.msg.errorEx(err);
        return EMPTY;
      }),
      shareReplay()
    );
  }
}
